import { ActivatedRoute } from '@angular/router';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatTooltip } from '@angular/material/tooltip';
import { map } from 'rxjs';
import { TodayStatusData } from 'src/app/models/today-status-data.model';
import { ApiService } from 'src/app/services/api.service';
import { EmployeeService } from 'src/app/services/employee.service';
import { VacationsComponent } from '../rrhh/absences-management/vacations/vacations.component';
import { CompanyProductObjectiveComponent } from './company-product-objective/company-product-objective.component';

type TodayStatusDataFormatted = TodayStatusData & { formatted_name: string };

@Component({
  selector: 'app-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatTooltip,
    VacationsComponent,
    CompanyProductObjectiveComponent,
    MatDatepickerModule,
    MatNativeDateModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
})
export class DashboardComponent {
  protected readonly page_title = inject(ActivatedRoute).snapshot.title;

  readonly selectedDate = signal<Date>(new Date());
  readonly maxDate: Date = new Date();

  private readonly api = inject(ApiService);
  private readonly employee = inject(EmployeeService);

  readonly today_data = toSignal(
    this.api
      .getUsersTodayStatus()
      .pipe(map((data: TodayStatusData[]) => data.map(d => ({ ...d, formatted_name: this.formatName(d.name, d.surnames) })))),
    { initialValue: [] as TodayStatusDataFormatted[] }
  );

  readonly active_company_objectives = toSignal(this.api.getMyActiveCompanyObjectives(new Date()), { initialValue: [] });

  readonly meeting_mode_enabled = toSignal(this.employee.meetingModeEnabled, { initialValue: false });
  readonly meeting_mode_company_product_id = toSignal(this.employee.meetingModeCompanyProduct, { initialValue: -1 });

  readonly active_company_objectives_for_view = computed(() => {
    const active_company_objectives = this.active_company_objectives();
    const meeting_mode_enabled = this.meeting_mode_enabled();
    const meeting_mode_company_product_id = this.meeting_mode_company_product_id();
    const should_filter_by_meeting_mode = meeting_mode_enabled && meeting_mode_company_product_id > 0;

    if (!should_filter_by_meeting_mode) {
      return active_company_objectives;
    }

    return active_company_objectives.filter(objective => objective.company_product_id === meeting_mode_company_product_id);
  });

  isToday(date: Date): boolean {
    const today = new Date();
    const d = new Date(date);
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth();
  }

  private formatName(name: string, surnames: string): string {
    const firstSurname = surnames.split(' ')[0];
    return `${name} ${firstSurname.charAt(0)}.`;
  }
}
